/**
 * TipTap node for homework draw.io blocks (same HTML as drawioHtml.ts).
 */
import { Node, mergeAttributes } from '@tiptap/core'
import {
  DRAWIO_BLOCK_CLASS,
  DRAWIO_PREVIEW_CLASS,
  DRAWIO_TITLE_CLASS,
  DrawioBlockValue,
  parseDrawioBlock,
} from '../homework/drawio/drawioHtml'

export type DrawioAttrs = {
  diagramId: string
  pngUrl: string
  xmlUrl: string
  title: string
  version: number
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    drawio: {
      insertDrawio: (value: DrawioBlockValue) => ReturnType
      updateDrawio: (value: DrawioBlockValue) => ReturnType
    }
  }
}

export const DrawioNode = Node.create({
  name: 'drawio',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      diagramId: { default: '' },
      pngUrl: { default: '' },
      xmlUrl: { default: '' },
      title: { default: 'Sơ đồ quy trình' },
      version: { default: 1 },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div.${DRAWIO_BLOCK_CLASS}`,
        getAttrs: (el) => {
          if (!(el instanceof HTMLElement)) return false
          const v = parseDrawioBlock(el)
          if (!v?.diagramId || !v.pngUrl) return false
          return {
            diagramId: v.diagramId,
            pngUrl: v.pngUrl,
            xmlUrl: v.xmlUrl || '',
            title: v.title || 'Sơ đồ quy trình',
            version: v.version || 1,
          }
        },
      },
    ]
  },

  renderHTML({ node }) {
    const value: DrawioBlockValue = {
      diagramId: node.attrs.diagramId,
      pngUrl: node.attrs.pngUrl,
      xmlUrl: node.attrs.xmlUrl,
      title: node.attrs.title,
      version: node.attrs.version,
    }
    const attrs = mergeAttributes({
      class: DRAWIO_BLOCK_CLASS,
      'data-diagram-id': value.diagramId,
      'data-png-url': value.pngUrl,
      'data-xml-url': value.xmlUrl,
      'data-version': String(value.version || 1),
      contenteditable: 'false',
    })
    // Return as HTMLSpec: tag + attrs + content from children structure
    return [
      'div',
      attrs,
      [
        'p',
        { class: DRAWIO_TITLE_CLASS },
        ['strong', {}, value.title || 'Sơ đồ quy trình'],
      ],
      [
        'img',
        {
          class: DRAWIO_PREVIEW_CLASS,
          src: value.pngUrl,
          alt: value.title || 'Sơ đồ quy trình',
          'data-diagram-id': value.diagramId,
          'data-xml-url': value.xmlUrl,
        },
      ],
      [
        'p',
        { class: 'hw-drawio-hint' },
        ['em', {}, 'Nhấn để chỉnh sửa sơ đồ (khi được phép sửa bài)'],
      ],
    ]
  },

  addCommands() {
    return {
      insertDrawio:
        (value: DrawioBlockValue) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              diagramId: value.diagramId,
              pngUrl: value.pngUrl,
              xmlUrl: value.xmlUrl,
              title: value.title || 'Sơ đồ quy trình',
              version: value.version || 1,
            },
          }),
      updateDrawio:
        (value: DrawioBlockValue) =>
        ({ tr, state, dispatch }) => {
          let updated = false
          state.doc.descendants((node, pos) => {
            if (node.type.name !== this.name) return
            if (node.attrs.diagramId !== value.diagramId) return
            if (dispatch) {
              tr.setNodeMarkup(pos, undefined, {
                diagramId: value.diagramId,
                pngUrl: value.pngUrl,
                xmlUrl: value.xmlUrl,
                title: value.title || 'Sơ đồ quy trình',
                version: value.version || 1,
              })
            }
            updated = true
          })
          if (updated && dispatch) dispatch(tr)
          return updated
        },
    }
  },
})
